/**
 * The text a share arrived with before its packaging was trimmed. Machine-made provenance, not the
 * person's writing, so it lives beside the note in this device's `meta` store rather than in the
 * note (no sync or schema change). Restore therefore works on the device that received the share.
 */
export interface ShareOriginalRecord {
  /** The trimmed text as it sits in the note body. */
  tidy: string
  /** The same share before trimming, formatted the same way. */
  original: string
  /** Which of the two the note currently holds. */
  mode: 'tidy' | 'original'
}

export const shareOriginalKey = (noteId: string) => `share-original:v1:${noteId}`

export function parseShareOriginal(value: unknown): ShareOriginalRecord | null {
  if (typeof value !== 'string') return null
  try {
    const parsed = JSON.parse(value) as Partial<ShareOriginalRecord>
    return typeof parsed.tidy === 'string' && typeof parsed.original === 'string'
      && (parsed.mode === 'tidy' || parsed.mode === 'original')
      ? { tidy: parsed.tidy, original: parsed.original, mode: parsed.mode }
      : null
  } catch { return null }
}

export function shareOriginalMetaRecord(noteId: string, record: ShareOriginalRecord) {
  return { key: shareOriginalKey(noteId), value: JSON.stringify(record) }
}

/** Swaps the trimmed text for the original (or back) inside a body that may have grown since arrival. */
export function swapShareText(body: string, record: ShareOriginalRecord, to: 'tidy' | 'original'): string | null {
  const from = to === 'original' ? record.tidy : record.original
  const next = to === 'original' ? record.original : record.tidy
  return body.includes(from) ? body.replace(from, next) : null
}

/** The strip is offered only while the note still holds the share's text. */
export function shareOriginalApplies(body: string, record: ShareOriginalRecord): boolean {
  return body.includes(record.mode === 'tidy' ? record.tidy : record.original)
}
